import { useCallback, useEffect, useRef } from "react";

export type AutosaveStatus = "idle" | "saving" | "saved" | "error";

interface Options {
  /** Saver returns when the network round-trip is done. Throw on failure. */
  save: () => Promise<void>;
  delayMs?: number;
  /** Called with state transitions so the UI can show "Speichern…" / "Gespeichert". */
  onStatus?: (status: AutosaveStatus, message?: string) => void;
}

/**
 * Schedules a debounced save and lets callers force-flush before navigating
 * away (e.g. when downloading PDF/DOCX, the legacy app calls `flushAutosave`
 * to make sure the server has the latest content before generating).
 */
export function useDebouncedAutosave({ save, delayMs = 1500, onStatus }: Options) {
  const timerRef = useRef<number | null>(null);
  const saveRef = useRef(save);
  const statusRef = useRef(onStatus);
  saveRef.current = save;
  statusRef.current = onStatus;

  const run = useCallback(async () => {
    timerRef.current = null;
    statusRef.current?.("saving");
    try {
      await saveRef.current();
      statusRef.current?.("saved");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      statusRef.current?.("error", message);
    }
  }, []);

  const schedule = useCallback(() => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => void run(), delayMs);
  }, [delayMs, run]);

  const flush = useCallback(async () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
      await run();
    }
  }, [run]);

  useEffect(() => () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
  }, []);

  return { schedule, flush };
}
